import { createContext, FC, useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import GlobalContext from './GlobalContext';
import { TMfaRecoveryPost, TMfaVerifyPost } from '../api/types';
import API from '../api/api';
import { EQueryStringParam, ERoutes } from '../util/constants';

type TMultiFactorAuthContext = {
    multiFactorAuthUiState: EMultiFactorAuthUiState;
    submitVerificationCode: (code: string) => Promise<void>;
    submitRecoveryCode: (code: string) => Promise<void>;
    redirectUrl: string;
    errorMessage: string;
};

export enum EMultiFactorAuthUiState {
    default,
    routerStateParsed,
    loading,
    ready,
    success,
    error,
    fatalError,
    disabled,
    expired,
}

export type TMultiFactorAuthRouterState = {
    redirectUrl: string;
};

export const MultiFactorAuthContext = createContext<TMultiFactorAuthContext | undefined>(undefined);

const MultiFactorAuthProvider: FC = ({ children }) => {
    const { getCsrfToken, csrfToken, language } = useContext(GlobalContext);
    const [multiFactorAuthUiState, setMultiFactorAuthUiState] = useState(EMultiFactorAuthUiState.default);
    const { state: routerState }: { state: TMultiFactorAuthRouterState } = useLocation();
    const [redirectUrl, setRedirectUrl] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const { search: queryParams, pathname } = useLocation();

    // -----------
    // Setup
    // -----------
    const parseRouterState = () => {
        if (!routerState?.redirectUrl) {
            setMultiFactorAuthUiState(EMultiFactorAuthUiState.fatalError);
        } else {
            setRedirectUrl(routerState.redirectUrl);
            setMultiFactorAuthUiState(EMultiFactorAuthUiState.routerStateParsed);
        }
    };

    const getInitialData = async () => {
        await getCsrfToken();
        setMultiFactorAuthUiState(EMultiFactorAuthUiState.ready);
    };

    const disableMFA = async () => {
        const params = new URLSearchParams(queryParams);
        const userId = params.get(EQueryStringParam.userId);
        const code = params.get(EQueryStringParam.code);
        if (!userId || !code) {
            console.error('Failed to parse query string', { cause: `Invalid query string ---> ${params.toString()}` });
            setMultiFactorAuthUiState(EMultiFactorAuthUiState.fatalError);
            return;
        }
        try {
            await API.delete.multiFactorDisable(userId, code);
            setMultiFactorAuthUiState(EMultiFactorAuthUiState.disabled);
        } catch (error) {
            if (error.response?.status === 400) {
                setMultiFactorAuthUiState(EMultiFactorAuthUiState.expired);
            } else if (error.response?.status === 409) {
                setMultiFactorAuthUiState(EMultiFactorAuthUiState.disabled);
            } else {
                setMultiFactorAuthUiState(EMultiFactorAuthUiState.fatalError);
            }
            console.error('Failed to disable MFA', error);
        }
    };
    useEffect(() => {
        switch (multiFactorAuthUiState) {
            case EMultiFactorAuthUiState.default:
                if (pathname === ERoutes.multiFactorAuthDisable) {
                    disableMFA();
                } else {
                    parseRouterState();
                }
                break;
            case EMultiFactorAuthUiState.routerStateParsed:
                getInitialData();
                break;
            default:
                break;
        }
    }, [multiFactorAuthUiState]);

    //------------
    // Submit verification code
    // -----------
    const submitVerificationCode = async (authenticatorCode: string) => {
        setMultiFactorAuthUiState(EMultiFactorAuthUiState.loading);
        try {
            const postObject: TMfaVerifyPost = {
                authenticatorCode,
            };
            await API.post.multiFactorVerify(postObject, csrfToken);
            setMultiFactorAuthUiState(EMultiFactorAuthUiState.success);
        } catch (error) {
            const { status } = error.response;
            if (status === 400 || status === 401) {
                setErrorMessage(language.multiFactorAuth.error.invalidCode);
                setMultiFactorAuthUiState(EMultiFactorAuthUiState.error);
            } else if (status === 429) {
                setErrorMessage(language.multiFactorAuth.error.accountLocked);
                setMultiFactorAuthUiState(EMultiFactorAuthUiState.error);
            } else {
                console.error('Failed to submit authenticator code');
                setMultiFactorAuthUiState(EMultiFactorAuthUiState.fatalError);
            }
        }
    };

    //------------
    // Submit recovery code
    // -----------
    const submitRecoveryCode = async (recoveryCode: string) => {
        setMultiFactorAuthUiState(EMultiFactorAuthUiState.loading);
        try {
            const postObject: TMfaRecoveryPost = {
                recoveryCode,
            };
            await API.post.multiFactorRecovery(postObject, csrfToken);
            setMultiFactorAuthUiState(EMultiFactorAuthUiState.success);
        } catch (error) {
            const { status } = error.response;
            if (status === 400 || status === 401) {
                setErrorMessage(language.multiFactorAuth.error.invalidCode);
                setMultiFactorAuthUiState(EMultiFactorAuthUiState.error);
            } else if (status === 429) {
                setErrorMessage(language.multiFactorAuth.error.accountLocked);
                setMultiFactorAuthUiState(EMultiFactorAuthUiState.error);
            } else {
                console.error('Failed to submit recovery code, ' + error);
                setMultiFactorAuthUiState(EMultiFactorAuthUiState.fatalError);
            }
        }
    };

    return (
        <MultiFactorAuthContext.Provider
            value={{ multiFactorAuthUiState, submitVerificationCode, submitRecoveryCode, errorMessage, redirectUrl }}
        >
            {children}
        </MultiFactorAuthContext.Provider>
    );
};

export default MultiFactorAuthProvider;
